import {
  ChangeEvent,
  FormEventHandler,
  memo,
  useCallback,
  useState,
} from 'react';
import NextImage from 'next/image';
import styled from 'styled-components';
import { useRouter } from 'next/router';

import { PriceFormHead } from './PriceForm';
import Title from 'components/common/title';
import Text from 'components/common/text';
import { Button } from 'components/common/button';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { useSignUp } from 'lib/mutation/sign-up';
import { PageLoader } from 'components/common/loader';
import Error from 'components/common/error';
import { resetOnboarding } from 'store/slices/onboarding';
import { addUser } from 'store/slices/auth';
import { useListing } from 'lib/mutation/listing';
import { onboardingSelector } from 'store/slices/onboarding/selector';
import { HOME_ROUTE } from 'constants/routes';

function ConfirmForm() {
  const router = useRouter();
  const [checked, setChecked] = useState(false);
  const { newUser, listing } = useAppSelector(onboardingSelector);
  const dispatch = useAppDispatch();
  const { isError, error, isLoading, signUp } = useSignUp();
  const {
    isError: isErrorListing,
    error: errorListing,
    isLoading: isLoadingListing,
    sendListing,
  } = useListing();

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        // Disabling rules because this part will be deprecated
        /* eslint-disable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain */
        const { jwt, user } = await signUp(newUser!);

        await sendListing({
          list_price: listing?.listPrice!,
          transBrokerComp: listing?.agentCommission!,
          address: newUser?.address!,
          jwt: jwt.access!,
        });
        /* eslint-enable */
        dispatch(resetOnboarding());
        dispatch(
          addUser({
            data: user,
            jwt: { ...jwt, access: `Bearer ${jwt.access}` },
          })
        );
        router.replace(HOME_ROUTE);
      } catch (err) {
        console.error(err);
      }
    },
    [newUser, signUp, dispatch, sendListing, listing, router]
  );

  function handleCheckboxChange(e: ChangeEvent<HTMLInputElement>) {
    setChecked(e.target.checked);
  }

  return (
    <>
      <PriceFormHead>
        <NextImage src="/static/icons/listing-2.svg" width={100} height={100} />
        <Title type="mode3" fw="light" className="price-form-title">
          One Step Closer to
        </Title>
        <Title type="mode3" fw="bold" className="price-form-subtitle">
          Selling Your Home
        </Title>
        <Text className="price-form-info">
          Please review and accept our Terms & Conditions, Privacy Policy,
          Affiliated Business Disclosure, and our Cancel Anytime Agreement.
        </Text>
      </PriceFormHead>
      <Confirm onSubmit={onSubmit}>
        <div className="checkbox-wrapper">
          <label className="checkbox" htmlFor="confirm-form-checkbox">
            <input
              type="checkbox"
              checked={checked}
              id="confirm-form-checkbox"
              onChange={handleCheckboxChange}
            />
            <span className="checkmark" />
          </label>

          <Text type="mode8">
            By listing your home you accept our
            <a
              href="https://richr.com/blog/terms-of-service/"
              target="_blank"
              rel="noreferrer"
            >
              Terms and Conditions
            </a>
            ,
            <a
              href="https://richr.com/blog/privacy-policy/"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
            ,
            <a
              href={`https://backend-stage.richr.com/api/preview-agreements/?type_pdf=affiliated&address=${newUser?.address}&list_price=${listing?.listPrice}&transBrokerComp=${listing?.agentCommission}&full_name=${newUser?.fullName}`}
              target="_blank"
              rel="noreferrer"
            >
              Affiliated Business Disclosure
            </a>
            <a
              href={`https://backend-stage.richr.com/api/preview-agreements/?type_pdf=limited&address=${newUser?.address}&list_price=${listing?.listPrice}&transBrokerComp=${listing?.agentCommission}&full_name=${newUser?.fullName}`}
              target="_blank"
              rel="noreferrer"
            >
              Listing Agreement
            </a>
          </Text>
        </div>

        <Button type="submit" className="submit" disabled={!checked}>
          <Text type="mode4">Review and Accept</Text>
        </Button>
        {isError && <Error data={error?.response?.data.errors} />}
        {isErrorListing && <Error data={errorListing?.response?.data.errors} />}
      </Confirm>
      {(isLoading || isLoadingListing) && <PageLoader />}
    </>
  );
}

const Confirm = styled.form`
  .checkbox-wrapper {
    display: flex;
    align-items: flex-start;
    > p {
      margin-left: 16px;
      text-align: left;
      > a {
        color: ${({ theme }) => theme.colors.green};
        text-decoration: underline;
        margin: 0 5px;
      }
    }
    .checkbox {
      display: block;
      position: relative;
      padding-left: 36px;
      cursor: pointer;
      font-size: 22px;
      user-select: none;
      input {
        position: absolute;
        opacity: 0;
        cursor: pointer;
        height: 0;
        width: 0;
        &:checked ~ .checkmark {
          background-color: ${({ theme }) => theme.colors.green};
        }
        &:checked ~ .checkmark:after {
          display: block;
        }
      }
      .checkmark {
        position: absolute;
        top: 8px;
        left: 0;
        height: 24px;
        width: 24px;
        background-color: ${({ theme }) => theme.colors.white};
        border: 1px solid ${({ theme }) => theme.colors.gray};
        &::after {
          content: '';
          position: absolute;
          display: none;

          left: 8px;
          top: 3px;
          width: 5px;
          height: 10px;
          border: solid white;
          border-width: 0 3px 3px 0;
          -webkit-transform: rotate(45deg);
          -ms-transform: rotate(45deg);
          transform: rotate(45deg);
        }
      }
    }
  }
  .submit {
    margin-top: 36px;
    border-radius: unset;
    max-width: 342px;
    margin: 0 auto;
    margin-top: 36px;
    > p {
      letter-spacing: 2px;
    }
    &:disabled {
      opacity: 0.4;
    }
  }
`;

export default memo(ConfirmForm);
